import LiveFeedback from './LiveFeedback';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import LiveDetailDialog from './LiveDetailDialog';
import LiveToolbarIcon from './LiveToolbarIcon';
import LiveChoiceButtons from './LiveChoiceButtons';
import LiveProductSelect from './LiveProductSelect';
import LiveCouponHistory from './LiveCouponHistory';
import { productRequestError } from '../productRequestError';

type CouponProduct = { seq: number; name: string; price?: number | string; individual_delivery?: boolean };
type CouponKind = 'standard' | 'linked_discount' | 'gift' | 'threshold';

export default function LiveDiscountCoupon({ liveSeq, disabled, products = [], submit }: { liveSeq: number; disabled: boolean; products?: CouponProduct[]; submit?: (payload: any) => Promise<{ data: any }> }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('1000');
  const [kind, setKind] = useState<CouponKind>('standard');
  const [discountType, setDiscountType] = useState<'amount' | 'percent'>('amount');
  const [sourceProduct, setSourceProduct] = useState('');
  const [targetProduct, setTargetProduct] = useState('');
  const [threshold, setThreshold] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [historyVersion, setHistoryVersion] = useState(0);
  const busy = useRef(false);
  const request = useRef<{ signature: string; id: string } | null>(null);
  const linked = kind === 'linked_discount' || kind === 'gift';
  const percentage = kind !== 'standard' && discountType === 'percent';
  const value = kind === 'gift' ? 100 : Number(amount);
  const valid = (kind === 'gift' || (amount !== '' && Number.isSafeInteger(value) && value > 0 && value <= (percentage ? 100 : 2147483647)))
    && (!linked || (Number(sourceProduct) > 0 && Number(targetProduct) > 0 && sourceProduct !== targetProduct
      && !products.find(product => product.seq === Number(targetProduct))?.individual_delivery))
    && (kind !== 'threshold' || (Number.isSafeInteger(Number(threshold)) && Number(threshold) > 0 && Number(threshold) <= 2147483647));
  const target = products.find(product => product.seq === Number(targetProduct));
  const targetPrice = Number(target?.price);
  const hasTargetPrice = linked && target && target.price != null && Number.isSafeInteger(targetPrice) && targetPrice >= 0;
  const previewValid = kind === 'gift' || (amount !== '' && Number.isSafeInteger(value) && value > 0 && value <= (percentage ? 100 : 2147483647));
  const targetDiscount = hasTargetPrice && previewValid ? Math.min(targetPrice, kind === 'gift' ? targetPrice
    : percentage ? Math.floor(targetPrice * value / 100) : value) : 0;
  const unit = percentage ? '%' : '원';
  const payload = { live_seq: liveSeq, amount: value, ...(kind === 'standard' ? {} : {
    kind, discount_type: kind === 'gift' ? 'percent' : discountType,
    ...(linked ? { source_product_seq: Number(sourceProduct), target_product_seq: Number(targetProduct) } : { threshold_amount: Number(threshold) }),
  }) };
  const grant = async () => {
    if (busy.current || disabled || !valid) return;
    busy.current = true; setSaving(true); setError(''); setMessage('');
    const signature = JSON.stringify(payload);
    if (!request.current || request.current.signature !== signature) request.current = { signature, id: crypto.randomUUID() };
    try {
      const requestPayload = { ...payload, request_id: request.current.id };
      const { data } = submit ? await submit(requestPayload) : await api.post(`${SERVER_ADDRESS}orders/discount-coupon`, requestPayload);
      if (!Number.isSafeInteger(data?.granted_count) || data.granted_count < 0 || !Number.isSafeInteger(data?.already_granted_count)) throw new Error('쿠폰 지급 결과를 확인하지 못했습니다. 같은 금액으로 다시 시도해주세요.');
      setMessage(`${data.granted_count}명에게 쿠폰을 지급했습니다.${data.already_granted_count > 0 ? ` 이미 받은 ${data.already_granted_count}명에게는 중복 지급하지 않았습니다.` : ''}`);
      request.current = null;
      if (submit) setOpen(false);
      else setHistoryVersion(version => version + 1);
    } catch (failure) { setError(productRequestError(failure, '할인 쿠폰을 지급하지 못했습니다. 같은 금액으로 다시 시도해주세요.')); }
    finally { busy.current = false; setSaving(false); }
  };
  return <>
    <AdminActionButton $accented type="button" disabled={disabled} aria-haspopup="dialog" onClick={() => { setOpen(true); setMessage(''); setError(''); }}><LiveToolbarIcon name="discount" />할인 쿠폰</AdminActionButton>
    <LiveDetailDialog footer={<AdminActionButton type="button" $primary disabled={!valid || disabled || saving} onClick={() => void grant()}>{saving ? '지급 중…' : kind === 'gift' ? '증정 쿠폰 제공' : `${valid ? value.toLocaleString() : '0'}${unit} 할인 쿠폰 제공`}</AdminActionButton>} open={open} title="할인 쿠폰" size={submit ? "medium" : "wide"} width={submit ? undefined : 1120} closeOnlyByButton={saving} onClose={() => { if (!busy.current) setOpen(false); }}>
      <Layout $withHistory={!submit}><Content>
        <LiveChoiceButtons label="쿠폰 종류" value={kind} disabled={saving} onChange={value => setKind(value as CouponKind)}
          options={[{ value: 'standard', label: '일반 할인 쿠폰' }, { value: 'linked_discount', label: '연계 상품 할인' },
            { value: 'gift', label: '연계 상품 증정' }, { value: 'threshold', label: '금액 달성 쿠폰' }]} />
        <Guidance><summary>쿠폰 적용 안내</summary>
          {kind === 'standard' && <p>지급 시점에 접속 중인 회원에게 방송당 한 번 지급합니다. 이미 받은 회원에게는 중복 지급하지 않습니다. 할인 금액은 구매 상품별로 자동 배분되어 적용됩니다.</p>}
          <p>지급 순간 해당 라이브에 접속 중인 회원만 대상입니다. 이후 입장한 회원에게는 적용되지 않습니다. 쿠폰은 해당 라이브에서 함께 사용할 수 있으며 개별 배송 상품은 할인에서 제외됩니다.</p>
          {linked && <p>{kind === 'gift' ? '연계 구매 조건을 충족한 고객만 B 상품 구매 버튼이 활성화됩니다. 고객은 옵션을 선택해 1개만 직접 구매하며 재고가 차감됩니다.'
            : '연계 구매 조건을 충족한 고객만 B 상품 구매 버튼이 활성화됩니다. 고객이 직접 옵션·수량을 선택하고 구매해야 재고가 차감됩니다.'}</p>}
        </Guidance>
        {linked && <ProductPair>
          <LiveProductSelect label="A 상품" value={sourceProduct} disabled={saving} onChange={setSourceProduct} products={products} />
          <LiveProductSelect label="B 상품" value={targetProduct} disabled={saving} onChange={setTargetProduct}
            products={products.filter(product => !product.individual_delivery && product.seq !== Number(sourceProduct))} />
        </ProductPair>}
        {kind === 'threshold' && <>
          <label>달성 금액<input aria-label="누적 구매금액 기준" type="number" min={1} max={2147483647} step={1} value={threshold} disabled={saving} onChange={event => setThreshold(event.target.value)} /><span>원</span></label>
          <p>해당 라이브의 고객 누적 구매금액을 모든 쿠폰 할인 전 금액으로 판단합니다.</p>
        </>}
        <DiscountFields $paired={kind !== 'standard' && kind !== 'gift'}>{kind !== 'standard' && kind !== 'gift' && <LiveChoiceButtons label="할인 방식" disabled={saving} value={discountType} onChange={value => {
          const next = value as 'amount' | 'percent'; setDiscountType(next);
          if (next === 'percent' && Number(amount) > 100) setAmount('10');
        }} options={[{ value: 'amount', label: '금액 할인' }, { value: 'percent', label: '비율 할인' }]} />}
        {kind === 'standard' && <AdminActionButton type="button" disabled={saving} aria-pressed={amount === '1000'} onClick={() => { setAmount('1000'); setMessage(''); }}>1,000원</AdminActionButton>}
        {kind !== 'gift' && <label className="amount"><span>{percentage ? '할인율' : '직접 입력'}</span><span className="input-unit"><input aria-label={percentage ? '할인 쿠폰 할인율' : '할인 쿠폰 금액'} type="number" min={1} max={percentage ? 100 : 2147483647} step={1} value={amount} disabled={saving}
          onChange={event => { setAmount(event.target.value); setMessage(''); }} /><span>{unit}</span></span></label>}</DiscountFields>
        {linked && <PricePreview aria-label="B 상품 금액 미리보기" aria-live="polite">
          <strong className="preview-title">B 상품 1개 기준</strong>
          {hasTargetPrice ? <>
            <div><span>상품 금액</span><span>{targetPrice.toLocaleString()}원</span></div>
            <div><span>할인액{percentage && kind !== 'gift' ? ` (${previewValid ? value : '—'}%)` : ''}</span><span>{previewValid ? `−${targetDiscount.toLocaleString()}원` : '—'}</span></div>
            <div className="final"><span>최종 B 상품 금액</span><strong>{previewValid ? `${(targetPrice - targetDiscount).toLocaleString()}원` : '—'}</strong></div>
            <p>{previewValid ? '이 쿠폰만 적용한 금액입니다. 다른 쿠폰·배송비는 포함하지 않습니다.' : '올바른 할인 금액 또는 할인율을 입력해주세요.'}</p>
          </> : <p>B 상품을 선택하면 할인 후 금액을 확인할 수 있습니다.</p>}
        </PricePreview>}
        {error && <LiveFeedback role="alert">{error}</LiveFeedback>}{message && <LiveFeedback role="status">{message}</LiveFeedback>}
        
      </Content>
      {!submit && open && <LiveCouponHistory liveSeq={liveSeq} refreshKey={historyVersion} disabled={disabled || saving}
        onBusyChange={value => { busy.current = value; setSaving(value); }} />}
      </Layout>
    </LiveDetailDialog>
  </>;
}

const Content = styled.div`
  display: flex; flex-direction: column; gap: var(--space-3); min-width: 0; font-size: var(--text-body);
  p { margin: 0; line-height: var(--leading-body); color: var(--color-text-secondary); }
  [role='alert'] { color: var(--color-text-danger); }
  [role='status'] { color: var(--color-success); }
  button[aria-pressed='true'] { border-color: var(--color-brand); background: var(--color-brand-subtle); color: var(--color-text-link); }
  label { display: flex; align-items: center; gap: var(--space-2); }
  input { flex: 1; min-width: 0; height: var(--control-lg); padding: 0 var(--space-3); font: inherit; border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

const Layout = styled.div<{ $withHistory: boolean }>`
  display: grid; grid-template-columns: ${({ $withHistory }) => $withHistory ? 'minmax(0, 1.35fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: var(--space-6); align-items: start;
  @media (max-width: 860px) { grid-template-columns: minmax(0, 1fr); }
`;
const ProductPair = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--space-3);
  > * { min-width: 0; }
  @media (max-width: 480px) { grid-template-columns: minmax(0, 1fr); }
`;
const DiscountFields = styled.div<{ $paired: boolean }>`
  display: grid; grid-template-columns: ${({ $paired }) => $paired ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: var(--space-3); align-items: end;
  > * { min-width: 0; }
  .amount { flex-direction: column; align-items: stretch; gap: var(--space-2); }
  .amount > span:first-child { font-weight: var(--font-weight-semibold); }
  .input-unit { display: flex; align-items: center; gap: var(--space-2); }
  @media (max-width: 480px) { grid-template-columns: minmax(0, 1fr); }
`;
const Guidance = styled.details`
  font-size: var(--text-caption); color: var(--color-text-secondary);
  summary { cursor: pointer; width: fit-content; padding: var(--space-1) 0; }
  summary:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; border-radius: var(--radius-sm); }
  p { margin-top: var(--space-2); }
`;
const PricePreview = styled.section`
  display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.3fr); gap: var(--space-2) var(--space-3);
  padding: var(--space-4); background: var(--color-bg-subtle); border-radius: var(--radius-md);
  .preview-title, p { grid-column: 1 / -1; }
  .preview-title { font-size: var(--text-caption); color: var(--color-text-secondary); font-weight: var(--font-weight-medium); }
  > div { display: flex; flex-direction: column; gap: var(--space-1); font-variant-numeric: tabular-nums; }
  > div > span:first-child { color: var(--color-text-secondary); font-size: var(--text-caption); }
  .final strong { color: var(--color-text-link); font-size: var(--text-h3); font-weight: var(--font-weight-bold); }
  p { font-size: var(--text-caption); }
  @media (max-width: 480px) { grid-template-columns: repeat(2, minmax(0, 1fr)); .final { grid-column: 1 / -1; flex-direction: row; justify-content: space-between; align-items: baseline; } }
`;
